/**
 * Admin Create Application Page
 * Allows admins to create job applications for candidates in the legacy system
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import MainLayout from '../components/Layout/MainLayout';
import { MdAdd, MdSearch, MdPerson, MdWork } from 'react-icons/md';
import applicationService from '../services/applicationService';
import candidateService from '../services/candidateService';
import { countryService, jobRoleService } from '../services/jobManagementService';

const AdminCreateApplicationPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [candidates, setCandidates] = useState([]);
  const [countries, setCountries] = useState([]);
  const [jobRoles, setJobRoles] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [formData, setFormData] = useState({
    candidateId: '',
    jobPosting: '',
    destinationCountry: '',
    questionnaire: [
      { question: 'Why are you interested in this position?', answer: '' },
      { question: 'What relevant experience do you have?', answer: '' },
      { question: 'When are you available to start?', answer: '' }
    ]
  });

  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      const [candidatesResponse, countriesResponse, jobRolesResponse] = await Promise.all([
        candidateService.getAllCandidates(),
        countryService.getAll(true), // Include inactive countries
        jobRoleService.getAll(true) // Include inactive job roles
      ]);

      if (candidatesResponse.success) {
        setCandidates(candidatesResponse.data);
      }
      if (countriesResponse.success) {
        setCountries(countriesResponse.data);
      }
      if (jobRolesResponse.success) {
        setJobRoles(jobRolesResponse.data);
      }
    } catch (error) {
      toast.error('Failed to load data');
      console.error('Load data error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCandidateSelect = (candidate) => {
    setSelectedCandidate(candidate);
    setFormData(prev => ({
      ...prev,
      candidateId: candidate._id
    }));
  };

  const handleJobRoleSelect = (jobRole) => {
    setFormData(prev => ({
      ...prev,
      jobPosting: jobRole.title,
      destinationCountry: jobRole.countryId?.name || ''
    }));
  };

  const handleQuestionnaireChange = (index, value) => {
    setFormData(prev => ({
      ...prev,
      questionnaire: prev.questionnaire.map((item, i) => 
        i === index ? { ...item, answer: value } : item
      )
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.candidateId) {
      toast.error('Please select a candidate');
      return;
    }
    
    if (!formData.jobPosting) {
      toast.error('Please select a job role');
      return;
    }

    try {
      setLoading(true);
      
      const applicationData = {
        candidateId: formData.candidateId,
        jobPosting: formData.jobPosting,
        destinationCountry: formData.destinationCountry,
        questionnaire: formData.questionnaire.filter(q => q.answer.trim())
      };
      
      console.log('🚀 Admin creating application:', applicationData);
      
      const response = await applicationService.createApplication(applicationData);
      
      if (response.success) {
        toast.success('Application created successfully!');
        navigate('/hr/applications');
      }
    } catch (error) {
      console.error('❌ Create application error:', error);
      const errorMessage = error.response?.data?.error || 'Failed to create application';
      toast.error(`Creation failed: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  const filteredCandidates = candidates.filter(candidate => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    const name = `${candidate.firstName || ''} ${candidate.lastName || ''}`.toLowerCase();
    const phone = candidate.phoneNumber?.toLowerCase() || '';
    const passport = candidate.passportNumber?.toLowerCase() || '';
    return name.includes(searchLower) || phone.includes(searchLower) || passport.includes(searchLower);
  });

  if (loading && candidates.length === 0) {
    return (
      <MainLayout>
        <div className="max-w-6xl mx-auto p-6">
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="text-gray-600 mt-4">Loading data...</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="max-w-6xl mx-auto p-6">
        <div className="mb-8">
          <button
            onClick={() => navigate('/admin/job-management')}
            className="text-blue-600 hover:text-blue-800 mb-4 flex items-center"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Job Management
          </button>
          
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Create Job Application</h1>
          <p className="text-gray-600">Create a new job application for a candidate</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Candidate Selection */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
              <MdPerson className="mr-2" />
              Select Candidate
            </h2>
            
            <div className="mb-4">
              <div className="relative">
                <MdSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search candidates by name, phone, or passport..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            {selectedCandidate && (
              <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800">
                  <strong>Selected:</strong> {selectedCandidate.firstName} {selectedCandidate.lastName}
                </p>
                <p className="text-xs text-blue-600">
                  {selectedCandidate.phoneNumber} | {selectedCandidate.passportNumber}
                </p>
              </div>
            )}

            <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-lg">
              {filteredCandidates.map(candidate => (
                <div
                  key={candidate._id}
                  onClick={() => handleCandidateSelect(candidate)}
                  className={`p-3 border-b border-gray-100 cursor-pointer hover:bg-gray-50 ${
                    selectedCandidate?._id === candidate._id ? 'bg-blue-50 border-blue-200' : ''
                  }`}
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="font-medium text-gray-900">
                        {candidate.firstName} {candidate.lastName}
                      </h3>
                      <p className="text-sm text-gray-600">{candidate.phoneNumber}</p>
                      <p className="text-xs text-gray-500">{candidate.passportNumber}</p>
                    </div>
                    <div className="text-xs text-gray-400">
                      {candidate.jobRole || 'No role specified'}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Application Form */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
              <MdWork className="mr-2" />
              Application Details
            </h2>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Job Role Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Job Role
                </label>
                <select
                  value={formData.jobPosting}
                  onChange={(e) => {
                    const selectedRole = jobRoles.find(role => role.title === e.target.value);
                    if (selectedRole) {
                      handleJobRoleSelect(selectedRole);
                    }
                  }}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="">Select a job role</option>
                  {jobRoles.map(role => (
                    <option key={role._id} value={role.title}>
                      {role.title} - {role.countryId?.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Destination Country
                </label>
                <input
                  type="text"
                  value={formData.destinationCountry}
                  onChange={(e) => setFormData(prev => ({ ...prev, destinationCountry: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                  readOnly
                />
              </div>

              {/* Questionnaire */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Application Questions</h3>
                {formData.questionnaire.map((item, index) => (
                  <div key={index} className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {item.question}
                    </label>
                    <textarea
                      value={item.answer}
                      onChange={(e) => handleQuestionnaireChange(index, e.target.value)}
                      rows={3}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter answer..."
                    />
                  </div>
                ))}
              </div>

              {/* Submit Button */}
              <div className="flex justify-end gap-4">
                <button
                  type="button"
                  onClick={() => navigate('/admin/job-management')}
                  className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading || !selectedCandidate || !formData.jobPosting}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Creating...
                    </>
                  ) : (
                    <>
                      <MdAdd className="mr-2" />
                      Create Application
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default AdminCreateApplicationPage;